/**
 * WhatsApp Business — configure modal.
 *
 * Opened from Settings → Channels → Integrations → "Configure" next to
 * WhatsApp Business. The modal:
 *
 *   • Starts in the disconnected/idle state — the dealer must explicitly
 *     click Connect to begin pairing.
 *   • While pairing, polls /v1/whatsapp/status every 3s and renders the QR.
 *   • Once linked, shows the phone number + a Disconnect button.
 *   • Polling and any open QR blob URLs are torn down when the modal closes
 *     or the component unmounts — no background loops, no hangs.
 *
 * Note: status field on the backend is `state` — the proxy normalizes
 * gateway statuses (LINKING/CONNECTED/...) into UI-friendly verbs
 * (qr/connected/disconnected/connecting/error).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { whatsappService } from '../lib/services';
import { useToast } from '../lib/toast';

const POLL_MS = 3000;

export default function WhatsAppConfigureModal({ open, onClose, onStateChange }) {
  const { toast } = useToast();
  const [status, setStatus]   = useState({ state: 'disconnected' });
  const [linking, setLinking] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const pollIdRef = useRef(null);
  const qrIdRef   = useRef(null);
  const blobRef   = useRef(null);
  const lastFetchRef = useRef(0);
  // Keep the latest callback in a ref so refreshStatus stays stable across
  // re-renders. Without this, every status fetch would invalidate the poll
  // effect and restart the interval, hammering the gateway.
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => { onStateChangeRef.current = onStateChange; }, [onStateChange]);

  /* ── Status fetch (stable identity — no deps) ── */
  const refreshStatus = useCallback(async () => {
    // Throttle: if a fetch happened in the last 500ms, skip. Belt-and-braces
    // against any future regression that re-triggers the effect rapidly.
    const now = Date.now();
    if (now - lastFetchRef.current < 500) return;
    lastFetchRef.current = now;
    try {
      const r = await whatsappService.status();
      const next = r?.data || r || { state: 'disconnected' };
      setStatus(next);
      onStateChangeRef.current?.(next);
    } catch (e) {
      setStatus({ state: 'unknown', error: e?.message });
    }
  }, []);

  /* ── QR fetch (stable identity) ── */
  const fetchQr = useCallback(async () => {
    try {
      const url = await whatsappService.qrBlob();
      blobRef.current = url;
      setQrBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      if (e?.response?.status === 204) return;
      console.warn('[whatsapp] QR fetch failed', e?.message);
    }
  }, []);

  /* ── Derived flags ── */
  const state    = (status?.state || 'disconnected').toLowerCase();
  const isPaired = state === 'connected' || status?.paired === true;
  const isPairing = state === 'qr' || state === 'connecting' || status?.qr === true;
  const isError  = state === 'error';
  const phone    = status?.phoneE164 || status?.phone || null;

  /* ── Lifecycle: poll only when modal is open ── */
  // NB: deps deliberately exclude refreshStatus — it's stable (useCallback
  // with []), so we only restart the interval when `open` flips.
  useEffect(() => {
    if (!open) return;
    refreshStatus();
    pollIdRef.current = setInterval(refreshStatus, POLL_MS);
    return () => {
      clearInterval(pollIdRef.current);
      pollIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Lifecycle: fetch QR while pairing ── */
  // Same deal — fetchQr is stable; only restart when open or isPairing flips.
  useEffect(() => {
    if (!open || !isPairing) {
      clearInterval(qrIdRef.current);
      qrIdRef.current = null;
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
      setQrBlobUrl(null);
      return;
    }
    fetchQr();
    qrIdRef.current = setInterval(fetchQr, POLL_MS);
    return () => {
      clearInterval(qrIdRef.current);
      qrIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPairing]);

  /* ── Final cleanup on unmount ── */
  useEffect(() => () => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
  }, []);

  /* ── Actions ── */
  const onConnect = async () => {
    setLinking(true);
    try {
      await whatsappService.link();
      toast('WhatsApp pairing started — scan the QR with your phone', { variant: 'success' });
      setTimeout(refreshStatus, 400);
    } catch (e) {
      toast(`Pairing failed: ${e?.response?.data?.error || e.message}`, { variant: 'danger' });
    } finally {
      setLinking(false);
    }
  };

  const onDisconnect = async () => {
    setBusy(true);
    try {
      await whatsappService.disconnect();
      toast('WhatsApp disconnected', { variant: 'success' });
      setTimeout(refreshStatus, 300);
    } catch (e) {
      toast(`Disconnect failed: ${e?.message}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  /* ── Render ── */
  const badgeText = isPaired
    ? `Connected${phone ? ` · ${phone}` : ''}`
    : isPairing ? 'Awaiting scan'
    : isError ? 'Error'
    : 'Not connected';
  const badgeBg = isPaired ? 'var(--success)'
    : isPairing ? 'var(--warning)'
    : isError   ? 'var(--danger)'
    : 'var(--text-tertiary)';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="wa-modal-title"
      onClick={onClose}
      className="modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal"
        style={{ width: 'min(720px, 92vw)' }}
      >
        <div className="modal-header">
          <div>
            <div id="wa-modal-title" style={{ fontSize: 16, fontWeight: 600 }}>
              WhatsApp Business · Configure
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Pair the dealership's WhatsApp account to the SpectraIQ AI agent.
            </div>
          </div>
          <div className="flex-row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="pill" style={{ background: badgeBg, color: 'white' }}>{badgeText}</span>
            <button
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Close"
            >✕</button>
          </div>
        </div>

        <div className="modal-body">
          {isPaired && (
            <PairedView
              phone={phone}
              busy={busy}
              onDisconnect={onDisconnect}
            />
          )}

          {!isPaired && (
            <PairingView
              isPairing={isPairing}
              linking={linking}
              qrBlobUrl={qrBlobUrl}
              onConnect={onConnect}
              onCancel={onClose}
              error={isError ? (status?.error || 'Pairing failed') : null}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */

function PairingView({ isPairing, linking, qrBlobUrl, onConnect, onCancel, error }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 24, alignItems: 'start' }}>
      <div style={{
        width: 240,
        height: 240,
        display: 'grid',
        placeItems: 'center',
        background: 'white',
        borderRadius: 8,
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        {isPairing && qrBlobUrl ? (
          <img src={qrBlobUrl} alt="WhatsApp pairing QR" style={{ width: 220, height: 220 }} />
        ) : isPairing ? (
          <div className="mono text-xs text-muted">Generating QR…</div>
        ) : (
          <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-tertiary)' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
            <div className="mono text-xs" style={{ color: 'var(--text-muted)' }}>
              Click <b>Connect</b> to generate a QR
            </div>
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          How to connect
        </div>
        <ol style={{
          margin: 0,
          paddingLeft: 18,
          lineHeight: 1.8,
          fontSize: 13,
          color: 'var(--text-secondary)',
        }}>
          <li>On the dealership's WhatsApp phone, open <b>Settings → Linked devices</b>.</li>
          <li>Tap <b>Link a device</b> and point the camera at the QR.</li>
          <li>Wait for the badge above to flip to <b>Connected</b>.</li>
        </ol>

        {error && (
          <div style={{
            marginTop: 12, padding: '8px 12px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid var(--danger)',
            borderRadius: 6,
            color: 'var(--danger)',
            fontSize: 12,
          }}>
            {error}
          </div>
        )}

        <div className="flex-row" style={{ marginTop: 18, gap: 8 }}>
          <button
            type="button"
            className="btn primary"
            disabled={linking || isPairing}
            onClick={onConnect}
          >
            {linking ? 'Starting…' : isPairing ? 'Waiting for scan…' : 'Connect'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>

        <div className="mono text-xs text-muted" style={{ marginTop: 14, lineHeight: 1.6 }}>
          Use a dedicated WhatsApp account. WhatsApp permits at most four
          linked devices per number; this gateway counts as one. The connection
          stays paired in the background — you can close this dialog at any time.
        </div>
      </div>
    </div>
  );
}

function PairedView({ phone, busy, onDisconnect }) {
  return (
    <div style={{ padding: 8 }}>
      <div style={{
        padding: 16,
        background: 'var(--bg-glass)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}>
        <div>
          <div className="mono text-xs text-muted">Active number</div>
          <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
            {phone || '—'}
          </div>
          <div className="mono text-xs text-muted" style={{ marginTop: 8, maxWidth: 480 }}>
            Inbound messages auto-route to the AI agent. Outbound messages stay
            inside WhatsApp's 24-hour conversation window unless the dealer is
            on the Twilio Business Solution Provider tier.
          </div>
        </div>
        <button
          type="button"
          className="btn danger"
          disabled={busy}
          onClick={onDisconnect}
        >
          {busy ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </div>
    </div>
  );
}
